import { useState } from "react";
import { Link } from "react-router-dom";
import { Lock, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { pointsBadgeClass } from "../../lib/badges";
import Countdown from "../../components/Countdown";
import ScoreStepper from "../../components/ScoreStepper";
import TeamCrest from "../../components/TeamCrest";
import { useI18n } from "../../i18n";
import { fmtTime, isLocked, teamShort } from "./utils";

export default function MatchCard({ match, teams, prediction, onSave, onDirtyChange, onDeleted, predictedSlot, poolId }) {
    const { t, lang } = useI18n();
    const home = teams[match.home_team_id];
    const away = teams[match.away_team_id];
    // Predicted-team overlay for TBD knockout slots. Only used when the
    // backend's /predicted-bracket has filled this slot (which it only does
    // when the relevant group is FULLY predicted).
    const ps = predictedSlot || {};
    const homePredicted = !home && ps.home_team_id ? teams[ps.home_team_id] : null;
    const awayPredicted = !away && ps.away_team_id ? teams[ps.away_team_id] : null;
    const homeName = home?.name || homePredicted?.name || "TBD";
    const awayName = away?.name || awayPredicted?.name || "TBD";
    // Resolved-or-predicted ids for the pen picker target list.
    const homeIdForPen = match.home_team_id || ps.home_team_id || null;
    const awayIdForPen = match.away_team_id || ps.away_team_id || null;

    const locked = isLocked(match.kickoff);
    // Draft overlay pattern — local edits live in `draft` until saved/reverted.
    // This avoids the set-state-in-effect lint blocker and also preserves
    // unsaved edits across unrelated background re-renders.
    const [draft, setDraft] = useState(null); // null when in-sync with prediction prop
    const h = draft?.h ?? prediction?.home_goals ?? 0;
    const a = draft?.a ?? prediction?.away_goals ?? 0;
    const pen = draft?.pen ?? prediction?.pen_winner_team_id ?? "";
    const dirty = draft != null;

    const updateDraft = (nextH, nextA, nextPen) => {
        // If next state matches the saved prediction, drop the draft entirely.
        const savedH = prediction?.home_goals;
        const savedA = prediction?.away_goals;
        const savedPen = prediction?.pen_winner_team_id ?? null;
        const samePen = (nextPen ?? null) === savedPen;
        const same = !!prediction && nextH === savedH && nextA === savedA && samePen;
        if (same) {
            setDraft(null);
            if (onDirtyChange) onDirtyChange(match.id, null);
            return;
        }
        setDraft({ h: nextH, a: nextA, pen: nextPen ?? null });
        if (onDirtyChange) onDirtyChange(match.id, { h: nextH, a: nextA, pen: nextPen ?? null });
    };

    const hasPred = !!prediction;
    const finished = match.status === "finished" && match.home_goals != null;
    const live = match.status === "live" && match.home_goals != null;
    const hasActual = finished || live;
    const isKnockout = match.stage === "knockout";
    const isDrawPred = h === a;
    // Show pen picker when the user has set a draw on a knockout match AND
    // we know both teams (resolved OR confidently predicted).
    const showPenPicker = isKnockout && isDrawPred && homeIdForPen && awayIdForPen && !hasActual;

    // ── Points-earned calc (mirrors backend scoring) ───────────────────────
    // Used inline when the match is live/finished AND the user had a prediction.
    let pointsEarned = null;
    if (hasActual && prediction) {
        const h2 = prediction.home_goals === match.home_goals ? 2 : 0;
        const a2 = prediction.away_goals === match.away_goals ? 2 : 0;
        const sign = (x, y) => x > y ? "H" : x < y ? "A" : "D";
        const o = sign(prediction.home_goals, prediction.away_goals) === sign(match.home_goals, match.away_goals) ? 3 : 0;
        pointsEarned = h2 + a2 + o;
    }

    return (
        <div
            data-testid={`match-card-${match.id}`}
            className={`rounded-lg border bg-[var(--bg-card)] p-4 transition-all duration-200 ${
                locked ? "border-[var(--border-muted)] opacity-90" : "border-[var(--border-base)] hover:border-[var(--accent-border)]"
            }`}
        >
            <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--text-muted)]">
                    {fmtTime(match.kickoff, lang)}
                </div>
                {locked ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-[#FF3B30]" data-testid={`locked-${match.id}`}>
                        <Lock className="h-3 w-3" /> {t("fix.locked")}
                    </span>
                ) : (
                    <Countdown kickoff={match.kickoff} compact />
                )}
            </div>

            <div className="flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0 text-right pr-3">
                    <div className={`font-heading text-base sm:text-lg font-bold uppercase tracking-tight truncate ${homePredicted ? "text-amber-400 italic" : "text-[var(--text-primary)]"}`}>
                        {homePredicted && <span className="mr-1" aria-hidden="true">💭</span>}
                        {homeName}
                    </div>
                    {home && <TeamCrest logo={home.logo} name={home.name} size="md" />}
                </div>

                <div className="flex flex-col items-center gap-1 min-w-[110px]">
                    {hasActual ? (
                        <>
                            {/* Locked prediction — small, muted, top */}
                            {hasPred ? (
                                <div
                                    data-testid={`pred-readout-${match.id}`}
                                    className="text-[10px] uppercase tracking-widest font-bold text-[var(--text-muted)] tabular-nums leading-none"
                                >
                                    {t("fix.predicted_short")} {prediction.home_goals}–{prediction.away_goals}
                                    {prediction.pen_winner_team_id && (
                                        <span className="ml-1 text-amber-400">
                                            ({teamShort(teams[prediction.pen_winner_team_id])})
                                        </span>
                                    )}
                                </div>
                            ) : (
                                <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--text-muted)] leading-none">
                                    {t("fix.no_pred_short")}
                                </div>
                            )}
                            {/* Actual / live score — prominent */}
                            <div className="flex items-center gap-1.5">
                                {live && (
                                    <span
                                        title="Live"
                                        data-testid={`live-dot-${match.id}`}
                                        className="inline-block h-2 w-2 rounded-full bg-[var(--danger)] animate-pulse"
                                    />
                                )}
                                <div
                                    data-testid={`actual-score-${match.id}`}
                                    className={`font-heading text-2xl font-extrabold tabular-nums leading-none ${live ? "text-[var(--danger)]" : "text-[var(--accent-hex)]"}`}
                                >
                                    {match.home_goals}–{match.away_goals}
                                </div>
                            </div>
                            {/* Points earned — inline, replaces the bottom-row badge */}
                            {pointsEarned != null && (
                                <span
                                    data-testid={`points-${match.id}`}
                                    className={`text-[10px] font-extrabold uppercase tracking-widest px-1.5 py-0.5 rounded ${pointsBadgeClass(pointsEarned)}`}
                                >
                                    +{pointsEarned} {t("fix.pts_short")}
                                </span>
                            )}
                        </>
                    ) : (
                        <div className="flex items-center gap-1.5">
                            <ScoreStepper testid={`pred-home-${match.id}`} value={h} disabled={locked} onChange={(v) => updateDraft(v, a, pen)} />
                            <span className="text-[var(--text-muted)] font-heading text-xl px-1">:</span>
                            <ScoreStepper testid={`pred-away-${match.id}`} value={a} disabled={locked} onChange={(v) => updateDraft(h, v, pen)} />
                        </div>
                    )}
                </div>

                <div className="flex-1 min-w-0 text-left pl-3">
                    <div className={`font-heading text-base sm:text-lg font-bold uppercase tracking-tight truncate ${awayPredicted ? "text-amber-400 italic" : "text-[var(--text-primary)]"}`}>
                        {awayPredicted && <span className="mr-1" aria-hidden="true">💭</span>}
                        {awayName}
                    </div>
                    {away && <TeamCrest logo={away.logo} name={away.name} size="md" />}
                </div>
            </div>

            {showPenPicker && (
                <div className="mt-3 p-2.5 rounded border border-amber-500/40 bg-amber-500/5" data-testid={`pen-picker-${match.id}`}>
                    <label className="block text-[10px] uppercase tracking-widest font-bold text-amber-400 mb-1.5" htmlFor={`pen-select-${match.id}`}>
                        {t("knock.pen.title")}
                    </label>
                    <select
                        id={`pen-select-${match.id}`}
                        data-testid={`pen-select-${match.id}`}
                        value={pen || ""}
                        onChange={(e) => updateDraft(h, a, e.target.value)}
                        className="w-full bg-[var(--bg-base)] border border-amber-500/50 text-[var(--text-primary)] text-sm font-extrabold uppercase tracking-widest px-2 py-1.5 rounded focus:outline-none focus:border-amber-500"
                    >
                        <option value="">{t("knock.pen.choose")}</option>
                        <option value={homeIdForPen}>{teamShort(teams[homeIdForPen])} — {teams[homeIdForPen]?.name || "?"}</option>
                        <option value={awayIdForPen}>{teamShort(teams[awayIdForPen])} — {teams[awayIdForPen]?.name || "?"}</option>
                    </select>
                </div>
            )}

            <div className="mt-3 flex items-center justify-between">
                <Link to={`/matches/${match.id}`} state={{ poolId }} data-testid={`match-detail-${match.id}`}
                    className="text-[10px] uppercase tracking-widest font-bold text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
                    {t("fix.details")}
                </Link>
                {!locked && !finished && (
                    <div className="inline-flex items-center gap-1">
                        {hasPred && poolId && (
                            <button
                                type="button"
                                title="Clear prediction"
                                onClick={async () => {
                                    try {
                                        await api.delete(`/pools/${poolId}/predictions/${match.id}`);
                                        toast.success("Prediction cleared");
                                        onDeleted?.(match.id);
                                    } catch (e) {
                                        toast.error(formatApiError(e.response?.data?.detail) || e.message);
                                    }
                                }}
                                data-testid={`delete-pred-${match.id}`}
                                className="p-1.5 rounded text-[var(--text-muted)] hover:text-[var(--danger)] hover:bg-[var(--overlay-light)] transition-colors"
                            >
                                <Trash2 className="h-3 w-3" />
                            </button>
                        )}
                        <button
                            data-testid={`save-pred-${match.id}`}
                            onClick={async () => {
                                // Await the parent's save so we don't optimistically
                                // clear the dirty state if the parent puts the save
                                // behind a "save all?" confirm modal.
                                const ok = await onSave(match.id, h, a, showPenPicker ? pen : null);
                                if (ok !== false) {
                                    setDraft(null);
                                    if (onDirtyChange) onDirtyChange(match.id, null);
                                }
                            }}
                            disabled={(!dirty && hasPred) || (showPenPicker && !pen)}
                            className="inline-flex items-center gap-1 bg-[var(--accent-hex)] text-[var(--accent-text)] text-[10px] font-extrabold uppercase tracking-widest px-3 py-1.5 rounded hover:bg-[var(--accent-hex-hover)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                            <Save className="h-3 w-3" /> {hasPred ? (dirty ? t("fix.update") : t("fix.saved")) : t("fix.save")}
                        </button>
                    </div>
                )}
                {/* Locked but no live/finished score yet (kickoff passed, awaiting data) */}
                {locked && !hasActual && prediction && (
                    <span data-testid={`pred-readout-bottom-${match.id}`}
                          className="text-[10px] uppercase tracking-widest font-bold text-[var(--text-muted)]">
                        {t("fix.predicted_short")} {prediction.home_goals}–{prediction.away_goals}
                    </span>
                )}
            </div>
        </div>
    );
}
